import type {
  Broadcast,
  BroadcastDelivery,
  ConversationMessage,
  DashboardStats,
  Proposal,
  ProposalRevision,
  PublicUser,
} from "@serezha168/shared";

const API_URL = import.meta.env.VITE_API_URL ?? "";
const browserSession = () =>
  window.localStorage.getItem("s168_session") ??
  window.sessionStorage.getItem("s168_admin_session");

function authorizationHeader(): Record<string, string> {
  const initData = window.Telegram?.WebApp.initData;
  if (initData) return { authorization: `tma ${initData}` };
  const session = browserSession();
  return session ? { authorization: `session ${session}` } : {};
}

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
  }
}

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const headers = new Headers(options.headers);
  if (!(options.body instanceof FormData) && !headers.has("content-type"))
    headers.set("content-type", "application/json");
  const authorization = authorizationHeader().authorization;
  if (authorization) headers.set("authorization", authorization);
  const response = await fetch(`${API_URL}${path}`, {
    ...options,
    headers,
    credentials: "include",
  });
  if (!response.ok) {
    const payload = (await response.json().catch(() => ({}))) as {
      error?: string;
    };
    throw new ApiError(
      payload.error ?? "Не удалось выполнить запрос",
      response.status,
    );
  }
  return response.json() as Promise<T>;
}

async function requestBlob(path: string): Promise<Blob> {
  const response = await fetch(`${API_URL}${path}`, {
    credentials: "include",
    headers: authorizationHeader(),
  });
  if (!response.ok) {
    const payload = (await response.json().catch(() => ({}))) as {
      error?: string;
    };
    throw new ApiError(
      payload.error ?? "Не удалось загрузить файл",
      response.status,
    );
  }
  return response.blob();
}

export const api = {
  me: () => request<{ user: PublicUser; isAdmin: boolean }>("/api/me"),
  consent: (broadcastEnabled = true) =>
    request<{ ok: true }>("/api/consent", {
      method: "POST",
      body: JSON.stringify({ broadcastEnabled }),
    }),
  setBroadcasts: (enabled: boolean) =>
    request<{ ok: true }>("/api/settings", {
      method: "PATCH",
      body: JSON.stringify({ broadcastEnabled: enabled }),
    }),
  proposals: () => request<{ proposals: Proposal[] }>("/api/proposals"),
  createProposal: (body: unknown) =>
    request<{ proposal: Proposal }>("/api/proposals", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  updateProposal: (id: string, body: unknown) =>
    request<{ proposal: Proposal }>(`/api/proposals/${id}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),
  contact: (text: string) =>
    request<{ ok: true }>("/api/contact", {
      method: "POST",
      body: JSON.stringify({ text }),
    }),
  upload: async (file: File) => {
    const response = await fetch(`${API_URL}/api/uploads`, {
      method: "POST",
      credentials: "include",
      headers: {
        ...authorizationHeader(),
        "content-type": file.type || "application/octet-stream",
        "x-file-name": encodeURIComponent(file.name),
        "x-file-size": String(file.size),
      },
      body: file,
    });
    const payload = (await response.json()) as { id?: string; error?: string };
    if (!response.ok || !payload.id)
      throw new ApiError(
        payload.error ?? "Не удалось загрузить файл",
        response.status,
      );
    return payload as {
      id: string;
      name: string;
      mimeType: string;
      size: number;
    };
  },
  adminLogin: (payload: unknown) =>
    request<{ ok: true; sessionToken: string }>("/api/auth/admin/telegram", {
      method: "POST",
      body: JSON.stringify(payload),
    }),
  createAdminChallenge: () =>
    request<{ id: string; botUrl: string; expiresAt: string }>(
      "/api/auth/admin/challenge",
      { method: "POST", body: "{}" },
    ),
  consumeAdminChallenge: (id: string) =>
    request<{ approved: boolean; sessionToken?: string }>(
      `/api/auth/admin/challenge/${id}/consume`,
      { method: "POST", body: "{}" },
    ),
  createWebChallenge: () =>
    request<{ id: string; botUrl: string; expiresAt: string }>(
      "/api/auth/web/challenge",
      { method: "POST", body: "{}" },
    ),
  consumeWebChallenge: (id: string) =>
    request<{ approved: boolean; sessionToken?: string }>(
      `/api/auth/web/challenge/${id}/consume`,
      { method: "POST", body: "{}" },
    ),
  logout: () =>
    request<{ ok: true }>("/api/auth/logout", { method: "POST", body: "{}" }),
  dashboard: () => request<{ stats: DashboardStats }>("/api/admin/dashboard"),
  conversations: (q = "", archived = false) =>
    request<{
      conversations: Array<{
        user: PublicUser;
        lastMessageAt?: string;
        lastMessagePreview?: string;
      }>;
    }>(
      `/api/admin/conversations?q=${encodeURIComponent(q)}&archived=${archived}`,
    ),
  messages: (userId: number, beforeCreated?: string, beforeId?: string) =>
    request<{
      messages: ConversationMessage[];
      hasMore: boolean;
      nextBeforeCreated?: string;
      nextBeforeId?: string;
    }>(
      `/api/admin/conversations/${userId}/messages${
        beforeCreated && beforeId
          ? `?beforeCreated=${encodeURIComponent(beforeCreated)}&beforeId=${encodeURIComponent(beforeId)}`
          : ""
      }`,
    ),
  messageFile: (messageId: string) =>
    requestBlob(`/api/admin/messages/${messageId}/file`),
  attachmentFile: (attachmentId: string) =>
    requestBlob(`/api/attachments/${attachmentId}`),
  sendMessage: (userId: number, body: unknown) =>
    request<{ messageId: string }>(
      `/api/admin/conversations/${userId}/messages`,
      {
        method: "POST",
        body: JSON.stringify(body),
      },
    ),
  users: (q = "") =>
    request<{ users: PublicUser[] }>(
      `/api/admin/users?q=${encodeURIComponent(q)}`,
    ),
  user: (userId: number) =>
    request<{
      user: PublicUser;
      proposals: Proposal[];
      messages: ConversationMessage[];
      messageCount: number;
      messagesHasMore: boolean;
      messagesNextBeforeCreated?: string;
      messagesNextBeforeId?: string;
      deliveries: Array<{
        status: string;
        error?: string;
        sent_at?: string;
        created_at: string;
        broadcast_id: string;
        title: string;
      }>;
    }>(`/api/admin/users/${userId}`),
  tags: () =>
    request<{
      tags: Array<{
        id: string;
        name: string;
        color: string;
        user_count: number;
      }>;
    }>("/api/admin/tags"),
  createTag: (name: string, color: string) =>
    request<{ id: string; name: string; color: string }>("/api/admin/tags", {
      method: "POST",
      body: JSON.stringify({ name, color }),
    }),
  setUserTags: (userId: number, tagIds: string[]) =>
    request<{ ok: true }>(`/api/admin/users/${userId}/tags`, {
      method: "PUT",
      body: JSON.stringify({ tagIds }),
    }),
  updateUserState: (userId: number, body: unknown) =>
    request<{ ok: true }>(`/api/admin/users/${userId}/state`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),
  deleteUser: (userId: number) =>
    request<{ ok: true }>(`/api/admin/users/${userId}`, {
      method: "DELETE",
      headers: { "x-confirm-user-id": String(userId) },
    }),
  adminProposals: (status = "") =>
    request<{ proposals: Proposal[] }>(
      `/api/admin/proposals${status ? `?status=${status}` : ""}`,
    ),
  proposalRevisions: (id: string) =>
    request<{ revisions: ProposalRevision[] }>(
      `/api/admin/proposals/${id}/revisions`,
    ),
  viewProposal: (id: string) =>
    request<{ ok: true }>(`/api/admin/proposals/${id}/view`, {
      method: "POST",
    }),
  updateProposalAdmin: (id: string, body: unknown) =>
    request<{ ok: true; notificationSent: boolean }>(
      `/api/admin/proposals/${id}`,
      { method: "PATCH", body: JSON.stringify(body) },
    ),
  broadcasts: () =>
    request<{ broadcasts: Broadcast[] }>("/api/admin/broadcasts"),
  broadcastDeliveries: (id: string) =>
    request<{ deliveries: BroadcastDelivery[] }>(
      `/api/admin/broadcasts/${id}/deliveries`,
    ),
  createBroadcast: (body: unknown) =>
    request<{ id: string; status: string; recipientTotal: number }>(
      "/api/admin/broadcasts",
      {
        method: "POST",
        body: JSON.stringify(body),
      },
    ),
  cancelBroadcast: (id: string) =>
    request<{ ok: true }>(`/api/admin/broadcasts/${id}/cancel`, {
      method: "POST",
    }),
  startBroadcast: (id: string) =>
    request<{ ok: true; recipientTotal: number; status: string }>(
      `/api/admin/broadcasts/${id}/start`,
      { method: "POST" },
    ),
  saveDraft: (kind: string, contextId: string, body: unknown) =>
    request<{ ok: true }>(`/api/admin/drafts/${kind}/${contextId}`, {
      method: "PUT",
      body: JSON.stringify(body),
    }),
  loadDraft: (kind: string, contextId: string) =>
    request<{ draft: Record<string, unknown> | null }>(
      `/api/admin/drafts/${kind}/${contextId}`,
    ),
  clearDraft: (kind: string, contextId: string) =>
    request<{ ok: true }>(`/api/admin/drafts/${kind}/${contextId}`, {
      method: "DELETE",
    }),
};
